import {Injectable} from "@angular/core";
import {map} from "rxjs/operators";
import {
  ConfirmOrderPlacedGqlModel,
  DeviceOrderSplitMutateGqlModel,
  DeviceOrderSplitResultModel,
  HardwareOrderDetailsInputModel,
  HardwareOrderDetailsMutateGqlModel,
  OrderDataGqlModel,
  OrderGqlModel,
  OrderListGqlModel,
  OrderMutateGqlModel,
  OrderRejectGqlModel,
  OrderRemoveGqlModel,
  OrderUnRejectGqlModel,
  OrderUpdateGqlModel,
  RequestOrderPlaceGqlModel,
  SiteListForOrderGqlModel, SubmitOrderResultMutateGqlModel,
} from "./order.gql-model";
import {Apollo} from "apollo-angular";
import {OrderFormGqlModel} from "./order-form.gql-model";
import {sequenceAsync} from "../misc/sequence.decorator";

const DEFAULT_PAGE = "1";
const DEFAULT_PAGE_LIMIT = "10";

@Injectable()
export class OrderService {
  constructor(private apollo: Apollo) {}

  @sequenceAsync()
  getOrders(
    page: string = DEFAULT_PAGE,
    limit: string = DEFAULT_PAGE_LIMIT,
    search: string = "",
    projectId: string = "",
    siteId: string = "",
    includeBreadcrumbs: boolean = false,
  ): Promise<OrderListGqlModel> {
    const query = OrderListGqlModel.query;
    const variables = OrderListGqlModel.variables({page, limit, search, projectId, siteId, includeBreadcrumbs});

    return this.apollo.query({query, variables, fetchPolicy: "no-cache"})
      .pipe(OrderListGqlModel.mapper)
      .toPromise();
  }

  getOrderById(id: string, includeBreadcrumbs: boolean = false): Promise<OrderDataGqlModel> {
    const query = OrderDataGqlModel.query;
    const variables = OrderDataGqlModel.variables(id, false, includeBreadcrumbs);

    return this.apollo.query({query, variables, fetchPolicy: "no-cache"})
      .pipe(OrderDataGqlModel.mapper)
      .toPromise();
  }

  getOrderForm(id: string): Promise<OrderFormGqlModel> {
    const query = OrderDataGqlModel.query;
    const variables = OrderDataGqlModel.variables(id, true);

    return this.apollo.query({query, variables, fetchPolicy: "no-cache"})
      .pipe(
        OrderDataGqlModel.mapper,
        map((order: OrderDataGqlModel) => order.orderForm || new OrderFormGqlModel({})),
      )
      .toPromise();
  }

  getAvailableSites(projectId: string): Promise<SiteListForOrderGqlModel[]> { // eslint-disable-line
    const query = SiteListForOrderGqlModel.query;
    const variables = SiteListForOrderGqlModel.variables(projectId);

    return this.apollo.query({query, variables, fetchPolicy: "no-cache"})
      .pipe(SiteListForOrderGqlModel.mapper)
      .toPromise();
  }

  save(order: OrderGqlModel): Promise<OrderGqlModel> {
    const orderInput = new OrderUpdateGqlModel(order);
    const model = new OrderMutateGqlModel(orderInput);

    return this.apollo.mutate({mutation: model.mutation, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(OrderMutateGqlModel.mapper)
      .toPromise();
  }

  confirmOrderForm(id: string): Promise<OrderGqlModel> {
    const model = new OrderMutateGqlModel(new RequestOrderPlaceGqlModel(id));

    return this.apollo.mutate({mutation: model.mutation, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(OrderMutateGqlModel.mapper)
      .toPromise();
  }

  confirmOrderPlaced(id: string): Promise<OrderGqlModel> {
    const model = new OrderMutateGqlModel(new ConfirmOrderPlacedGqlModel(id));

    return this.apollo.mutate({mutation: model.mutation, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(OrderMutateGqlModel.mapper)
      .toPromise();
  }

  remove(id: string): Promise<OrderGqlModel> {
    const model = new OrderMutateGqlModel(new OrderRemoveGqlModel(id));

    return this.apollo.mutate({mutation: model.mutation, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(OrderMutateGqlModel.mapper)
      .toPromise();
  }

  create(name: string, siteId: string): Promise<OrderGqlModel> {
    const order = new OrderGqlModel({name, siteId});
    return this.save(order);
  }

  updateOrderNumbersAndDates(orderId: string, details: HardwareOrderDetailsInputModel[]): Promise<boolean> {
    const model = new HardwareOrderDetailsMutateGqlModel({orderId, details});

    return this.apollo.mutate({mutation: model.mutation, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(
        HardwareOrderDetailsMutateGqlModel.mapper,
        map(result => result.success),
      )
      .toPromise();
  }

  splitDevice(deviceId, splitId, orderId, quantities): Promise<DeviceOrderSplitResultModel[]> {
    const model = new DeviceOrderSplitMutateGqlModel({deviceId, orderId, splitId, quantities});

    return this.apollo.mutate({mutation: model.mutation, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(DeviceOrderSplitMutateGqlModel.mapper)
      .toPromise();
  }

  autoSplitDevice(deviceId, splitId, orderId): Promise<DeviceOrderSplitResultModel[]> {
    const model = new DeviceOrderSplitMutateGqlModel({deviceId, orderId, splitId});

    return this.apollo.mutate({mutation: model.mutation, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(DeviceOrderSplitMutateGqlModel.mapper)
      .toPromise();
  }

  rejectOrder(id: string, reason: string): Promise<OrderGqlModel> {
    const orderInput = new OrderRejectGqlModel(id, reason);
    const model = new OrderMutateGqlModel(orderInput);

    return this.apollo.mutate({mutation: model.mutation, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(OrderMutateGqlModel.mapper)
      .toPromise();
  }

  unRejectOrder(id: string): Promise<OrderGqlModel> {
    const orderInput = new OrderUnRejectGqlModel(id);
    const model = new OrderMutateGqlModel(orderInput);

    return this.apollo.mutate({mutation: model.mutation, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(OrderMutateGqlModel.mapper)
      .toPromise();
  }

  submitOrderToItsm(id): any {
    const model = new SubmitOrderResultMutateGqlModel({orderId: id});

    return this.apollo.mutate({mutation: model.mutation, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(SubmitOrderResultMutateGqlModel.mapper)
      .toPromise();
  }
}
